import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Save, Upload, Loader2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  company_name: 'GO MO Group DBS', tagline: 'Digital Business Solutions',
  address: '', city: '', state: '', country: '', pincode: '',
  phone: '', email: '', website: '', gst_number: '', pan_number: '',
  bank_name: '', bank_account: '', bank_ifsc: '', bank_branch: ''
};

export default function CompanySettingsPage() {
  const [form, setForm] = useState({ ...EMPTY });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/company-settings');
      if (data && data.company_name) setForm(prev => ({ ...prev, ...data }));
      // Try to load logo
      try {
        const resp = await fetch(`${API_URL}/api/company-settings/logo`, { credentials: 'include' });
        if (resp.ok) {
          const blob = await resp.blob();
          setLogoUrl(URL.createObjectURL(blob));
        }
      } catch {}
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try { await api.post('/company-settings', form); toast.success('Company settings saved'); }
    catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/company-settings/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo uploaded');
      // Refresh logo preview
      const resp = await fetch(`${API_URL}/api/company-settings/logo`, { credentials: 'include' });
      if (resp.ok) { const blob = await resp.blob(); setLogoUrl(URL.createObjectURL(blob)); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const f = (field) => ({ value: form[field] || '', onChange: e => setForm({ ...form, [field]: e.target.value }) });

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="company-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Company Profile</h1>
        <p className="text-sm text-muted-foreground mt-1">Set up your company logo and details for invoices and purchase orders</p>
      </div>

      {/* Logo Section */}
      <Card data-testid="logo-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><ImageIcon size={18} /> Company Logo</CardTitle>
          <CardDescription>Upload your company logo to appear on invoices and POs (max 2MB, PNG/JPG)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/30" data-testid="logo-preview">
              {logoUrl ? (
                <img src={logoUrl} alt="Company Logo" className="w-full h-full object-contain" />
              ) : (
                <Building2 size={32} className="text-muted-foreground" />
              )}
            </div>
            <div>
              <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2" data-testid="upload-logo-button">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                {logoUrl ? 'Change Logo' : 'Upload Logo'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">Recommended: 200x200px, transparent PNG</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Details */}
      <Card data-testid="company-details-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Building2 size={18} /> Company Details</CardTitle>
          <CardDescription>This information appears on your invoices and purchase orders</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-foreground/80 text-sm">Company Name *</Label>
              <Input {...f('company_name')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-foreground/80 text-sm">Tagline</Label>
              <Input {...f('tagline')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-tagline-input" placeholder="e.g. Digital Business Solutions" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-foreground/80 text-sm">Address</Label>
            <Input {...f('address')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-address-input" placeholder="Street address" />
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label className="text-foreground/80 text-sm">City</Label>
              <Input {...f('city')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-city-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-foreground/80 text-sm">State</Label>
              <Input {...f('state')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-state-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-foreground/80 text-sm">Pincode</Label>
              <Input {...f('pincode')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-pincode-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-foreground/80 text-sm">Country</Label>
              <Input {...f('country')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-country-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-foreground/80 text-sm">Phone</Label>
              <Input {...f('phone')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-foreground/80 text-sm">Email</Label>
              <Input {...f('email')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-foreground/80 text-sm">Website</Label>
              <Input {...f('website')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-website-input" />
            </div>
          </div>

          {/* Tax Registration */}
          <div className="pt-2 border-t border-border">
            <p className="text-sm font-medium text-foreground/80 mb-3">Tax Registration</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-foreground/80 text-sm">GST Number</Label>
                <Input {...f('gst_number')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-gst-input" placeholder="e.g. 22AAAAA0000A1Z5" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground/80 text-sm">PAN Number</Label>
                <Input {...f('pan_number')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-pan-input" />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="pt-2 border-t border-border">
            <p className="text-sm font-medium text-foreground/80 mb-3">Default Bank Details (shown on invoices)</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-foreground/80 text-sm">Bank Name</Label>
                <Input {...f('bank_name')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-bank-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground/80 text-sm">Account Number</Label>
                <Input {...f('bank_account')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-bank-account-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground/80 text-sm">IFSC Code</Label>
                <Input {...f('bank_ifsc')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-bank-ifsc-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground/80 text-sm">Branch</Label>
                <Input {...f('bank_branch')} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="co-bank-branch-input" />
              </div>
            </div>
          </div>

          <div className="pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="save-company-settings-button">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save Company Profile
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
